import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, ArrowRight, BookOpen, CheckCircle2, XCircle, Loader2,
  ClipboardCheck, PartyPopper, RotateCcw,
} from "lucide-react";

const STAGE = { READ: "read", QUIZ: "quiz", RESULT: "result" };

export default function ModuleReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [stage, setStage] = useState(STAGE.READ);
  const [section, setSection] = useState(0);

  // quiz state
  const [quiz, setQuiz] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/modules/${id}`).then(({ data }) => setMod(data)).catch(() => setMod(false));
    api.post(`/modules/${id}/start`).catch(() => {});
  }, [id]);

  const sections = mod?.sections || [];
  const readProgress = sections.length ? Math.round(((section + 1) / sections.length) * 100) : 0;

  const startQuiz = async () => {
    setStage(STAGE.QUIZ);
    setLoadingQuiz(true);
    try {
      const { data } = await api.get(`/modules/${id}/quiz`);
      setQuiz(data.questions);
    } catch (e) {
      toast.error("The quiz is still being prepared. Please try again in a moment.");
      setStage(STAGE.READ);
    } finally {
      setLoadingQuiz(false);
    }
  };

  const submitQuiz = async () => {
    if (Object.keys(answers).length < (quiz?.length || 0)) {
      toast.error("Please answer all questions before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const ordered = quiz.map((_, i) => answers[i]);
      const { data } = await api.post(`/modules/${id}/attempt`, { answers: ordered });
      setResult(data);
      setStage(STAGE.RESULT);
    } catch (e) {
      toast.error("Could not submit your answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setAnswers({});
    setResult(null);
    setQuiz(null);
    setSection(0);
    setStage(STAGE.READ);
  };

  if (mod === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  if (mod === false) return <p className="py-20 text-center text-muted-foreground">Module not found.</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <button
        data-testid="back-to-dashboard"
        onClick={() => navigate("/")}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> All training
      </button>

      {stage === STAGE.READ && (
        <ReadView
          mod={mod}
          sections={sections}
          section={section}
          setSection={setSection}
          readProgress={readProgress}
          onFinish={startQuiz}
        />
      )}

      {stage === STAGE.QUIZ && (
        <QuizView
          mod={mod}
          quiz={quiz}
          loading={loadingQuiz}
          answers={answers}
          setAnswers={setAnswers}
          submitting={submitting}
          onSubmit={submitQuiz}
          onBack={() => setStage(STAGE.READ)}
        />
      )}

      {stage === STAGE.RESULT && <ResultView mod={mod} result={result} onRetake={retake} onDone={() => navigate("/")} />}
    </div>
  );
}

function ReadView({ mod, sections, section, setSection, readProgress, onFinish }) {
  const s = sections[section];
  const last = section === sections.length - 1;
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-primary">
        <BookOpen className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em]">Training document</span>
      </div>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight">{mod.title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{mod.description}</p>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Section {section + 1} of {sections.length}
          </span>
          <span>{readProgress}%</span>
        </div>
        <Progress value={readProgress} className="h-2 bg-secondary" />
      </div>

      <AnimatePresence mode="wait">
        <motion.article
          key={section}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="mt-5 rounded-2xl border border-border bg-card p-6 shadow-[0_8px_30px_rgba(37,75,54,0.06)]"
          data-testid={`section-${section}`}
        >
          <h2 className="font-heading text-xl font-bold tracking-tight text-primary">{s?.heading}</h2>
          <p className="mt-3 whitespace-pre-line leading-relaxed text-foreground/90">{s?.body}</p>
        </motion.article>
      </AnimatePresence>

      <div className="sticky bottom-24 z-20 mt-6 flex gap-3 md:bottom-6">
        {section > 0 && (
          <Button
            data-testid="prev-section-button"
            variant="secondary"
            className="h-12 flex-1 rounded-xl"
            onClick={() => setSection((v) => v - 1)}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
        )}
        {!last ? (
          <Button
            data-testid="next-section-button"
            className="h-12 flex-1 rounded-xl hover:bg-primary/90 active:scale-[0.98]"
            onClick={() => setSection((v) => v + 1)}
          >
            Next section <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button
            data-testid="start-quiz-button"
            className="h-12 flex-1 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 active:scale-[0.98]"
            onClick={onFinish}
          >
            <ClipboardCheck className="mr-1.5 h-5 w-5" /> Take the quiz
          </Button>
        )}
      </div>
    </div>
  );
}

function QuizView({ mod, quiz, loading, answers, setAnswers, submitting, onSubmit, onBack }) {
  const letters = ["A", "B", "C"];
  if (loading || !quiz)
    return (
      <div className="flex flex-col items-center gap-3 py-20 text-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Preparing your quiz…</p>
      </div>
    );

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-accent">
        <ClipboardCheck className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em]">5-question quiz · pass mark 80%</span>
      </div>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight">{mod.title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">Answer all 5 questions based on what you just read.</p>

      <div className="mt-6 space-y-5">
        {quiz.map((q, qi) => (
          <motion.div
            key={qi}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: qi * 0.05 }}
            className="rounded-2xl border border-border bg-card p-5 shadow-[0_8px_30px_rgba(37,75,54,0.06)]"
            data-testid={`quiz-question-${qi}`}
          >
            <p className="font-heading font-bold tracking-tight">
              <span className="text-accent">{qi + 1}.</span> {q.question}
            </p>
            <div className="mt-3 space-y-2">
              {q.options.map((opt, oi) => {
                const selected = answers[qi] === oi;
                return (
                  <button
                    key={oi}
                    data-testid={`quiz-${qi}-option-${oi}`}
                    onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors duration-200 active:scale-[0.99] ${
                      selected ? "border-primary bg-primary/5" : "border-border bg-background hover:bg-secondary"
                    }`}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        selected ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
                      }`}
                    >
                      {letters[oi]}
                    </span>
                    <span className="text-sm">{opt}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="sticky bottom-24 z-20 mt-6 flex gap-3 md:bottom-6">
        <Button data-testid="quiz-back-button" variant="secondary" className="h-12 rounded-xl px-6" onClick={onBack}>
          <BookOpen className="mr-1.5 h-4 w-4" /> Review
        </Button>
        <Button
          data-testid="submit-quiz-button"
          className="h-12 flex-1 rounded-xl hover:bg-primary/90 active:scale-[0.98]"
          disabled={submitting}
          onClick={onSubmit}
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit answers"}
        </Button>
      </div>
    </div>
  );
}

function ResultView({ mod, result, onRetake, onDone }) {
  const { passed, score, correct, total, review } = result;
  const letters = ["A", "B", "C"];
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <div
        data-testid="quiz-result"
        className={`rounded-2xl border p-6 text-center shadow-[0_8px_30px_rgba(37,75,54,0.06)] ${
          passed ? "border-primary/20 bg-primary/5" : "border-destructive/20 bg-destructive/5"
        }`}
      >
        <span
          className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${
            passed ? "bg-primary text-primary-foreground" : "bg-destructive text-destructive-foreground"
          }`}
        >
          {passed ? <PartyPopper className="h-8 w-8" /> : <RotateCcw className="h-8 w-8" />}
        </span>
        <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight">
          {passed ? "Congratulations! 🎉" : "Not quite yet"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {passed
            ? `You passed "${mod.title}". This has been added to your training record.`
            : `You need at least 80% to pass. Review the material and try again — you've got this.`}
        </p>
        <p className="mt-4 font-heading text-5xl font-black" data-testid="result-score">
          {score}%
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {correct} of {total} correct
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          {passed ? (
            <Button
              data-testid="result-done-button"
              className="h-12 rounded-xl px-8 hover:bg-primary/90 active:scale-[0.98]"
              onClick={onDone}
            >
              Back to my training
            </Button>
          ) : (
            <>
              <Button
                data-testid="retake-quiz-button"
                className="h-12 rounded-xl px-8 hover:bg-primary/90 active:scale-[0.98]"
                onClick={onRetake}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" /> Review & retake
              </Button>
              <Button data-testid="result-exit-button" variant="secondary" className="h-12 rounded-xl px-8" onClick={onDone}>
                Later
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Your answers</p>
        {review.map((q, qi) => {
          const right = q.selected === q.answer_index;
          return (
            <div key={qi} className="rounded-2xl border border-border bg-card p-4" data-testid={`review-${qi}`}>
              <div className="flex items-start gap-2">
                {right ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                ) : (
                  <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                )}
                <p className="font-medium">{q.question}</p>
              </div>
              <div className="mt-2 space-y-1 pl-7 text-sm">
                {q.options.map((opt, oi) => {
                  const isCorrect = oi === q.answer_index;
                  const isChosen = oi === q.selected;
                  return (
                    <p
                      key={oi}
                      className={`rounded-lg px-2 py-1 ${
                        isCorrect
                          ? "bg-primary/10 font-semibold text-primary"
                          : isChosen
                          ? "bg-destructive/10 text-destructive line-through"
                          : "text-muted-foreground"
                      }`}
                    >
                      {letters[oi]}. {opt}
                      {isCorrect && " ✓"}
                    </p>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
